import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { Field, Formik, Form } from 'formik';
import * as Yup from 'yup';
import { PATH_CLIENT_EDIT, PATH_CLIENT_MEASUREMENTS } from 'routing/path'
import PageHeader from 'components/page-header'
import ValidatedField from 'components/controls/validated-field';
import ValidatedDateField from 'components/controls/validated-date-field';
import ValidatedMaskedField from 'components/controls/validated-masked-field';
import ValidatedSelectField from 'components/controls/validated-select-field';
import DosemeterMeasurementPeriodEndField from 'components/controls/custom/dosemeter-measurement-period-end-field';
import { showSuccessNotification, showErrorNotification } from "actions/action-complete-notificator";
import { useGetNewDosemeterQuery, useAddDosemeterMutation } from 'actions/client/client-dosemeter-measurement-api';
import { useGetFacilitiesQuery } from 'actions/client/client-facility-api';
import { translationApi  } from 'actions/translation-api';
import { getDosemeterTypeDescription, getDosemeterTypeCode, DOSEMETER_TYPE_ENVIRONMENTAL, DOSEMETER_TYPE_PERSONAL, DOSEMETER_TYPE_PERSONAL_WRIST } from 'data/enums/dosemeter-type-enum';
import { tryParseInt } from 'utils/number-utils';

const ClientDosemeterAdd = () => {
	const navigate = useNavigate();
	const params = useParams();
	const clientIdString = params.clientId;
	const clientId = tryParseInt(clientIdString);
	const dosemeterTypeString = params.dosemeterType;
	const dosemeterType = tryParseInt(dosemeterTypeString);

	const newDosemeterRequest = {
		clientId: clientId,
		dosemeterType: dosemeterType
	};
	const { data: newDosemeter, isLoading: loadNewDosemeterInProgress, isFetching: fetchNewDosemeterInProgress } = useGetNewDosemeterQuery(newDosemeterRequest, { refetchOnMountOrArgChange: true } );
	const newDosemeterReady = !loadNewDosemeterInProgress && !fetchNewDosemeterInProgress && newDosemeter;
	
	const clientFacilitiesRequest = {
		clientId: clientId,
		page: 1,
		pageSize: 1000
	};
	const { data: clientFacilities, isLoading: loadClientFacilitiesInProgress, isFetching: fetchClientFacilitiesInProgress } = useGetFacilitiesQuery(clientFacilitiesRequest, { refetchOnMountOrArgChange: true } );
	const clientFacilitiesReady = !loadClientFacilitiesInProgress && !fetchClientFacilitiesInProgress && clientFacilities;
	
	const [ addDosemeter, { isLoading: addDosemeterInProgress } ] = useAddDosemeterMutation();
	const translations = useSelector(translationApi.endpoints.getTranslations.select()).data ?? {};

	const getPageHeader = () => {
		// waiting for API response
		if (!newDosemeter) {
			return <PageHeader title="..." />;
		}

		let title = `${translations.newDosemeter} ${newDosemeter?.clientId?.toString().padStart(4, '0')}-${getDosemeterTypeCode(dosemeterType)}${newDosemeter?.clientDosemeterNumber} (${newDosemeter?.clientName})`;
		const breadcrumbs = [
			{ name: `${translations.client} ${newDosemeter?.clientName}`, link: PATH_CLIENT_EDIT + newDosemeter?.clientId },
		];

		return <PageHeader title={title} breadcrumbs={breadcrumbs} breadcrumbsCurrentPage={`${translations.newDosemeter} ${newDosemeter?.clientId?.toString().padStart(4, '0')}-${getDosemeterTypeCode(dosemeterType)}${newDosemeter?.clientDosemeterNumber}`} />
	}

	const initialValues = {
		clientId: newDosemeterReady ? newDosemeter.clientId?.toString().padStart(4, '0') : "",
		number: newDosemeterReady ? newDosemeter.clientDosemeterNumber : "",
		measurementPeriodStart: newDosemeterReady ? newDosemeter.firstMeasurementPeriodStart : "",
		measurementPeriodEnd: newDosemeterReady ? newDosemeter.firstMeasurementPeriodEnd : "",
		dosemeterType: newDosemeterReady ? newDosemeter.dosemeterType : "",
		clientFacility: clientFacilitiesReady && clientFacilities.clientFacilities.length > 0 ? clientFacilities.clientFacilities[0].clientFacilityId : "",
		firstName:  "",
		lastName: "",
	};
	
	var clientFacilitiesToDropDownList = clientFacilitiesReady ?
		clientFacilities.clientFacilities.map(function (clientFacility) {
			return [ clientFacility.clientFacilityId, clientFacility.name ]
		}) : null;

	let fourDigitNumberRegex = /^\d{4}$/;
	let fourDigitNumberInputMask = ['0', '0', '0', '0'];
	let dosemeterTypesToDropDownList = [
		[dosemeterType, getDosemeterTypeDescription(dosemeterType, translations)]
	];
	
	const validationSchema = Yup.object().shape({
		clientId: Yup.string()
			.required(translations.warningFieldRequired)
			.matches(fourDigitNumberRegex, translations.warningValueInvalid),
		dosemeterType: Yup.number()
			.required(translations.warningFieldRequired),
		number: Yup.string()
			.required(translations.warningFieldRequired)
			.matches(fourDigitNumberRegex, translations.warningValueInvalid),
		measurementPeriodStart: Yup.string()
			.required(translations.warningFieldRequired),
		firstName: Yup.string()
			.when("dosemeterType", {
				is: dosemeterType => dosemeterType == DOSEMETER_TYPE_PERSONAL || dosemeterType == DOSEMETER_TYPE_PERSONAL_WRIST,
				then: () => Yup.string().required(translations.warningFieldRequired)
			}),
		lastName: Yup.string()
			.when("dosemeterType", {
				is: dosemeterType => dosemeterType == DOSEMETER_TYPE_PERSONAL || dosemeterType == DOSEMETER_TYPE_PERSONAL_WRIST,
				then: () => Yup.string().required(translations.warningFieldRequired)
			}),
	});
	
	const submitDisabled = !newDosemeterReady || !clientFacilitiesReady || addDosemeterInProgress;
	const handleSubmit = async (fields) => {
		const dosemeterToInsert = {
			clientId: clientId,
			clientFacilityId: tryParseInt(fields.clientFacility),
			clientDosemeterNumber: fields.number,
			firstMeasurementPeriodStart: fields.measurementPeriodStart,
			firstMeasurementPeriodEnd: fields.measurementPeriodEnd,
            dosemeterType: tryParseInt(fields.dosemeterType),
			firstName: fields.firstName,
			lastName: fields.lastName
		};
		
		const result = await addDosemeter(dosemeterToInsert);
		if ('error' in result) {
			showErrorNotification(result.error);
		}
		else {
			showSuccessNotification(translations.dosemeterAdded);
			navigate(PATH_CLIENT_MEASUREMENTS + clientId);
			return;
		}
	}

	const randomAutoComplete = Math.random().toString(36).replace(/[^a-z]+/g, "").substr(0, 5);
	return (
		<div>
			{ getPageHeader()}
			{ <Formik
				enableReinitialize
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={ async (fields) => { await handleSubmit(fields) }}>
				{props => (
					<Form autoComplete={randomAutoComplete}>
					<div className="col-12 mb-4">
						<div className="card border-light shadow-sm components-section">
							<div className="card-body">
								<h2 className="h5 mb-4">{translations.basicData}</h2>
								<div className="row mb-4">
									<div className="col-lg-4 col-sm-6">
										<ValidatedMaskedField name="clientId" mask={fourDigitNumberInputMask} label={translations.clientNumber} readOnly={true} {...props} />
									</div>
									<div className="col-lg-4 col-sm-6">
										<ValidatedSelectField name="dosemeterType" label={translations.type} keyValueMap={dosemeterTypesToDropDownList} readOnly={true} {...props} />
									</div>
									<div className="col-lg-4 col-sm-6">
										<ValidatedMaskedField name="number" mask={fourDigitNumberInputMask} label={translations.number} readOnly={true} {...props} />
									</div>
									{clientFacilitiesToDropDownList && clientFacilitiesToDropDownList.length > 0 &&
										<div className="col-lg-4 col-sm-6">
											<ValidatedSelectField name="clientFacility" label={translations.clientFacility} keyValueMap={clientFacilitiesToDropDownList} {...props} />
										</div>
									}
								</div>
								{(dosemeterType === DOSEMETER_TYPE_PERSONAL || dosemeterType === DOSEMETER_TYPE_PERSONAL_WRIST) &&
									<div className="row mb-4">
										<Field name="dosemeterType" type="hidden" /> 
										<div className="col-lg-4 col-sm-6">
											<ValidatedField name="firstName" label={translations.firstName} {...props} />
										</div>
										<div className="col-lg-4 col-sm-6">
											<ValidatedField name="lastName" label={translations.lastName} {...props} />
										</div>
									</div>
								}
								<h2 className="h5 mb-4">{translations.measurementPeriod}</h2>
								<div className="row mb-4">
									<div className="col-lg-4 col-sm-6">
										<ValidatedDateField name="measurementPeriodStart" label={translations.measurementPeriodStart} {...props} />
									</div>
									<div className="col-lg-4 col-sm-6">
										<DosemeterMeasurementPeriodEndField name="measurementPeriodEnd" readOnly={true} label={translations.measurementPeriodEnd} {...props} />
									</div>
								</div>
								<div className="row mb-5 mb-lg-5">
									<div className="col-lg-3 col-md-6">
										<div className="form-group mb-3">
											<button id="saveButton" type="submit" className="btn btn-primary me-2" disabled={submitDisabled ? "disabled" : null} >{translations.actionAdd}</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</Form>)}
			</Formik>}
		</div>
	)
} 

export default ClientDosemeterAdd;
